/**
 * DTF Transfer by Size – Area Price Calculator
 * Uses Upload Lift / CloudLift as the live pricing source and mirrors the area tier lookup in the UI.
 * UI total formula: area × effective price per sq inch × quantity.
 */
(function () {
  'use strict';

  var PRODUCT_CONFIGS = {
    'dtf-transfer-by-size': { originalRate: 0.08 },
    'dtf-transfer-by-size-staging': { originalRate: 0.08 },
    'uv-dtf-stickers-by-size': { originalRate: 0.08 },
    'uv-dtf-stickers-by-size-staging': { originalRate: 0.08 }
  };

  var FALLBACK_AREA_TIERS = [
    { min: 0, max: 19.99, rate: 0.06, rateDisplay: '0.06', label: '0–19.99' },
    { min: 20, max: 699.99, rate: 0.03, rateDisplay: '0.03', label: '20–699.99' },
    { min: 700, max: 1599.99, rate: 0.022, rateDisplay: '0.022', label: '700–1599.99' },
    { min: 1600, max: 2999.99, rate: 0.02, rateDisplay: '0.02', label: '1600–2999.99' },
    { min: 3000, max: null, rate: 0.018, rateDisplay: '0.018', label: '3000+' }
  ];

  function getProductConfig(handle) {
    return PRODUCT_CONFIGS[handle] || null;
  }

  function formatMoney(dollars) {
    var n = typeof dollars === 'number' ? dollars : parseFloat(dollars, 10);
    if (isNaN(n)) return '$0.00';
    var cents = Math.round(n * 100);
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents);
    }
    return '$' + n.toFixed(2);
  }

  function formatArea(area) {
    if (typeof area !== 'number' || isNaN(area) || area <= 0) return '0.00 sq in';
    return area.toFixed(2) + ' sq in';
  }

  function formatRate(rateDisplay) {
    return '$' + rateDisplay + ' / sq in';
  }

  function roundToCents(dollars) {
    return Math.round((Number(dollars) || 0) * 100) / 100;
  }

  function parseAreaTiersAttr(table) {
    if (!table) return null;
    var raw = table.getAttribute('data-dtf-area-tiers');
    if (!raw || !raw.trim()) return null;
    var parts = raw.split(',');
    var tiers = [];
    for (var i = 0; i < parts.length; i++) {
      var p = (parts[i] || '').trim();
      if (!p) continue;
      var pipeIdx = p.indexOf('|');
      if (pipeIdx < 0) continue;
      var range = p.substring(0, pipeIdx).trim();
      var rateDisplay = p.substring(pipeIdx + 1).trim();
      var rate = parseFloat(rateDisplay, 10);
      if (isNaN(rate)) continue;
      var min = 0, max = null, label = range;
      var plusIdx = range.indexOf('+');
      if (plusIdx >= 0) {
        min = parseFloat(range.substring(0, plusIdx).trim(), 10) || 0;
        max = null;
        label = min + '+';
      } else {
        var dashIdx = range.indexOf('-');
        if (dashIdx >= 0) {
          min = parseFloat(range.substring(0, dashIdx).trim(), 10) || 0;
          max = parseFloat(range.substring(dashIdx + 1).trim(), 10);
          label = String(min) + '–' + String(max);
        } else {
          min = parseFloat(range, 10) || 0;
          max = min;
          label = String(min);
        }
      }
      tiers.push({ min: min, max: max, rate: rate, rateDisplay: rateDisplay, label: label });
    }
    return tiers.length ? tiers : null;
  }

  function getAreaTiers() {
    var table = document.querySelector('[data-dtf-volume-table]') || document.querySelector('.dmixer-tier-table');
    return parseAreaTiersAttr(table) || FALLBACK_AREA_TIERS;
  }

  function getActiveAreaTierIndex(tiers, area) {
    if (!tiers || !tiers.length) return 0;
    var currentArea = typeof area === 'number' && !isNaN(area) ? area : 0;
    for (var i = 0; i < tiers.length; i++) {
      var t = tiers[i];
      if (t.max === null && currentArea >= t.min) return i;
      if (currentArea >= t.min && currentArea <= t.max) return i;
    }
    return 0;
  }

  function getVariantPricePerSqIn(wrap) {
    var cents = parseInt(wrap.getAttribute('data-variant-price-cents'), 10);
    if (!isNaN(cents) && cents > 0) return cents / 100;
    return 0;
  }

  /**
   * CloudLift/Live Product Options fires options:price when the app calculates the price
   * (e.g. from dimension-based formula). Use that as base instead of flat $0.04/sq in.
   * Stored per product so we can fall back to formula if event never fires.
   */
  var cloudliftUnitPrice = null;

  function setCloudliftBasePrice(priceInDollars) {
    if (typeof priceInDollars === 'number' && !isNaN(priceInDollars) && priceInDollars >= 0) {
      cloudliftUnitPrice = roundToCents(priceInDollars);
    }
  }

  function clearCloudliftBasePrice() {
    cloudliftUnitPrice = null;
  }

  function getFormDimensionInputs(formId) {
    var form = formId ? document.getElementById(formId) : null;
    var selX = 'input[name="properties[_dimension_x]"]';
    var selY = 'input[name="properties[_dimension_y]"]';
    var dimX = (form && form.querySelector(selX)) || (formId && document.querySelector(selX + '[form="' + formId + '"]')) || document.querySelector(selX);
    var dimY = (form && form.querySelector(selY)) || (formId && document.querySelector(selY + '[form="' + formId + '"]')) || document.querySelector(selY);
    var dimensionContainer = document.querySelector('[data-option="dimension"]');
    if ((!dimX || !dimY) && dimensionContainer) {
      var inputs = dimensionContainer.querySelectorAll('input[type="text"], input:not([type="hidden"])');
      if (inputs.length >= 2) {
        dimX = dimX || inputs[0];
        dimY = dimY || inputs[1];
      }
      if (!dimX || !dimY) {
        var byIdX = dimensionContainer.querySelector('input[id$="-x"]');
        var byIdY = dimensionContainer.querySelector('input[id$="-y"]');
        if (byIdX && byIdY) {
          dimX = dimX || byIdX;
          dimY = dimY || byIdY;
        }
      }
    }
    return { x: dimX, y: dimY };
  }

  function getDimensionFromHidden(formId) {
    var form = formId ? document.getElementById(formId) : null;
    var sel = 'input[name="properties[dimension]"]';
    var hidden = (form && form.querySelector(sel)) || (formId && document.querySelector(sel + '[form="' + formId + '"]')) || document.querySelector(sel);
    if (!hidden || !hidden.value) return null;
    var parts = (hidden.value || '').trim().split(/\s*[x×]\s*/i);
    if (parts.length >= 2) {
      var w = parseFloat(parts[0], 10);
      var h = parseFloat(parts[1], 10);
      if (!isNaN(w) && !isNaN(h) && w > 0 && h > 0) return { w: w, h: h };
    }
    return null;
  }


  function init() {
    var wrap = document.getElementById('dtf-price-calculator-root');
    var handle = wrap && wrap.getAttribute('data-product-handle');
    var productConfig = getProductConfig(handle);
    if (!wrap || !productConfig) return false;

    var formId = wrap.getAttribute('data-form-id') || '';

  var liveAreaEl = wrap.querySelector('[data-dtf-calc="live-area"]');
  var liveRateEl = wrap.querySelector('[data-dtf-calc="live-rate"]');
    var liveUnitEl = wrap.querySelector('[data-dtf-calc="live-unit"]');
    var buyButtonsWrap = wrap.closest('.product-form__buy-buttons');
    var summaryUnitEl = buyButtonsWrap && buyButtonsWrap.querySelector('[data-dtf-calc="summary-unit"]');
    var summaryUnitOriginalEl = buyButtonsWrap && buyButtonsWrap.querySelector('[data-dtf-calc="summary-unit-original"]');
    var summaryTotalEl = buyButtonsWrap && buyButtonsWrap.querySelector('[data-dtf-calc="summary-total"]');
    var summaryTotalOriginalEl = buyButtonsWrap && buyButtonsWrap.querySelector('[data-dtf-calc="summary-total-original"]');
    var liveCloudliftUnitEl = wrap.querySelector('[data-dtf-calc="live-cloudlift-unit"]');
    var liveOriginalTotalEl = wrap.querySelector('[data-dtf-calc="live-total-original"]');
    var liveTotalEl = wrap.querySelector('[data-dtf-calc="live-total"]');

    if (!liveAreaEl || !liveRateEl || !liveUnitEl || !liveTotalEl) return false;

    function getWidthHeightFromForm() {
      var dims = getFormDimensionInputs(formId);
      var w = 0;
      var h = 0;
      if (dims.x && dims.x.value) { w = parseFloat(String(dims.x.value).trim(), 10); }
      if (dims.y && dims.y.value) { h = parseFloat(String(dims.y.value).trim(), 10); }
      if (isNaN(w) || w < 0.001 || isNaN(h) || h < 0.001) {
        var fromHidden = getDimensionFromHidden(formId);
        if (fromHidden) {
          w = fromHidden.w;
          h = fromHidden.h;
        }
      }
      w = isNaN(w) || w < 0.001 ? 0 : w;
      h = isNaN(h) || h < 0.001 ? 0 : h;
      return { w: w, h: h };
    }

    function getAreaInfo() {
      var dims = getWidthHeightFromForm();
      var pieceArea = dims.w > 0 && dims.h > 0 ? dims.w * dims.h : 0;
      return { width: dims.w, height: dims.h, pieceArea: pieceArea };
    }

    function getFallbackCloudliftUnitPrice(area) {
      var pricePerSqIn = getVariantPricePerSqIn(wrap);
      if (area <= 0.01 || pricePerSqIn <= 0) return 0;
      return roundToCents(pricePerSqIn * area);
    }

    function findQtyInput() {
      /* Prefer quantity in same buy-buttons as calculator (ensures we're synced with visible quantity) */
      if (wrap && wrap.closest) {
        var buyButtons = wrap.closest('.product-form__buy-buttons');
        if (buyButtons) {
          var qty = buyButtons.querySelector('[data-dtf-calc-form-qty]') || buyButtons.querySelector('input[name="quantity"]');
          if (qty) return qty;
        }
      }
      var formEl = formId ? document.getElementById(formId) : (wrap && document.contains(wrap) && wrap.closest('form'));
      if (formEl) {
        var qty = formEl.querySelector('[data-dtf-calc-form-qty]') || formEl.querySelector('input[name="quantity"]');
        if (qty) return qty;
      }
      if (formId) {
        return document.querySelector('[data-dtf-calc-form-qty][form="' + formId + '"]') || document.querySelector('input[name="quantity"][form="' + formId + '"]');
      }
      return null;
    }

    function getQtyFromForm() {
      var input = findQtyInput();
      if (!input) return 1;
      var val = (input.value || '').trim();
      var q = parseInt(val, 10);
      if (isNaN(q) || q < 1) return 1;
      return q;
    }

    function updateDiscountMixerTable(activeTierIdx) {
      var table = document.querySelector('[data-dtf-volume-table]');
      if (!table) return;
      var rows = table.querySelectorAll('[data-dtf-area-row]');
      var priceCells = table.querySelectorAll('.dmixer-volume-table-cell-unit-price');
      var areaCells = table.querySelectorAll('.dmixer-volume-table-cell-quantity');
      for (var i = 0; i < rows.length; i++) {
        var isActive = i === activeTierIdx;
        rows[i].classList.toggle('dtf-dmixer-tier-row--active', isActive);
        if (priceCells[i]) priceCells[i].classList.toggle('dtf-dmixer-tier--active', isActive);
        if (areaCells[i]) areaCells[i].classList.toggle('dtf-dmixer-tier--active', isActive);
      }
    }

    function recalc() {
      var areaInfo = getAreaInfo();
      var q = getQtyFromForm();
      var pieceArea = areaInfo.pieceArea;
      var totalArea = pieceArea * q;
      var tiers = getAreaTiers();
      var tierIdx = getActiveAreaTierIndex(tiers, totalArea);
      var activeTier = tiers[tierIdx] || FALLBACK_AREA_TIERS[0];
      var effectiveRate = Number(activeTier.rate) || 0;
      var originalRate = productConfig.originalRate;
      var originalUnitPrice = originalRate != null && pieceArea > 0 ? roundToCents(pieceArea * originalRate) : 0;
      var unitPrice = pieceArea > 0 ? roundToCents(pieceArea * effectiveRate) : 0;
      var originalTotal = originalRate != null && totalArea > 0 ? roundToCents(totalArea * originalRate) : 0;
      var total = totalArea > 0 ? roundToCents(totalArea * effectiveRate) : 0;
      var cloudliftDisplayPrice = cloudliftUnitPrice != null ? cloudliftUnitPrice : getFallbackCloudliftUnitPrice(pieceArea);

      updateDiscountMixerTable(tierIdx);

      var originalUnitStr = formatMoney(originalUnitPrice);
      var unitStr = formatMoney(unitPrice);
      var originalTotalStr = formatMoney(originalTotal);
      var totalStr = formatMoney(total);
      liveAreaEl.textContent = formatArea(totalArea);
      liveRateEl.textContent = formatRate(activeTier.rateDisplay || String(effectiveRate));
      liveUnitEl.textContent = unitStr + ' ea';
      if (summaryUnitEl) {
        summaryUnitEl.textContent = unitStr;
      }
      if (summaryUnitOriginalEl) {
        summaryUnitOriginalEl.textContent = originalUnitStr;
      }
      if (liveOriginalTotalEl) {
        liveOriginalTotalEl.textContent = originalTotalStr;
      }
      liveTotalEl.textContent = totalStr;
      if (summaryTotalEl) {
        summaryTotalEl.textContent = totalStr;
      }
      if (summaryTotalOriginalEl) {
        summaryTotalOriginalEl.textContent = originalTotalStr;
      }
      liveAreaEl.setAttribute('data-live-format', liveAreaEl.textContent);
      liveRateEl.setAttribute('data-live-format', liveRateEl.textContent);
      if (liveUnitEl.setAttribute) {
        liveUnitEl.setAttribute('data-live-format', unitStr);
      }
      if (liveCloudliftUnitEl && liveCloudliftUnitEl.setAttribute) {
        liveCloudliftUnitEl.textContent = cloudliftDisplayPrice > 0 ? formatMoney(cloudliftDisplayPrice) + ' ea' : '—';
        liveCloudliftUnitEl.setAttribute('data-live-format', liveCloudliftUnitEl.textContent);
      }
      if (liveOriginalTotalEl && liveOriginalTotalEl.setAttribute) {
        liveOriginalTotalEl.setAttribute('data-live-format', originalTotalStr);
      }
      if (liveTotalEl.setAttribute) {
        liveTotalEl.setAttribute('data-live-format', totalStr);
      }
      if (summaryUnitEl && summaryUnitEl.setAttribute) {
        summaryUnitEl.setAttribute('data-live-format', unitStr);
      }
      if (summaryUnitOriginalEl && summaryUnitOriginalEl.setAttribute) {
        summaryUnitOriginalEl.setAttribute('data-live-format', originalUnitStr);
      }
      if (summaryTotalEl && summaryTotalEl.setAttribute) {
        summaryTotalEl.setAttribute('data-live-format', totalStr);
      }
      if (summaryTotalOriginalEl && summaryTotalOriginalEl.setAttribute) {
        summaryTotalOriginalEl.setAttribute('data-live-format', originalTotalStr);
      }
      [50, 150, 350, 600, 1000].forEach(function (ms) {
        setTimeout(function () {
          if (wrap && document.contains(wrap)) {
            updateDiscountMixerTable(tierIdx);
          }
        }, ms);
      });

      var dimInputs = getFormDimensionInputs(formId);
      if (dimInputs.x && !dimInputs.x.dataset.dtfListener) {
        dimInputs.x.dataset.dtfListener = '1';
        dimInputs.x.addEventListener('input', recalc);
        dimInputs.x.addEventListener('change', recalc);
        dimInputs.x.addEventListener('keyup', recalc);
      }
      if (dimInputs.y && !dimInputs.y.dataset.dtfListener) {
        dimInputs.y.dataset.dtfListener = '1';
        dimInputs.y.addEventListener('input', recalc);
        dimInputs.y.addEventListener('change', recalc);
        dimInputs.y.addEventListener('keyup', recalc);
      }
      var dimContainer = document.querySelector('[data-option="dimension"]');
      if (dimContainer && !dimContainer.dataset.dtfClickListener) {
        dimContainer.dataset.dtfClickListener = '1';
        dimContainer.addEventListener('click', function () {
          setTimeout(recalc, 0);
        });
      }
      /* Direct listeners on quantity input (typing) + quantity-selector (for +/- buttons which set value programmatically) */
      var qtyInput = findQtyInput();
      if (qtyInput && !qtyInput.dataset.dtfQtyListener) {
        qtyInput.dataset.dtfQtyListener = '1';
        qtyInput.addEventListener('input', recalc);
        qtyInput.addEventListener('change', recalc);
        qtyInput.addEventListener('keyup', recalc);
        qtyInput.addEventListener('keydown', function () {
          setTimeout(recalc, 0);
        });
        qtyInput.addEventListener('blur', recalc);
      }
      var qtySelector = (wrap.closest && wrap.closest('form')) ? wrap.closest('form').querySelector('quantity-selector') : null;
      if (qtySelector && !qtySelector.dataset.dtfQtyClickListener) {
        qtySelector.dataset.dtfQtyClickListener = '1';
        qtySelector.addEventListener('click', function () {
          setTimeout(recalc, 0);
        });
      }
      /* Fallback: any click in buy-buttons triggers recalc (catches +/- and any missed events) */
      var buyButtons = wrap.closest && wrap.closest('.product-form__buy-buttons');
      if (buyButtons && !buyButtons.dataset.dtfBuyButtonsListener) {
        buyButtons.dataset.dtfBuyButtonsListener = '1';
        buyButtons.addEventListener('click', function () {
          setTimeout(recalc, 0);
        });
      }
    }

    var qtyListenersAttached = false;
    function attachFormQtyListeners() {
      if (qtyListenersAttached) return;
      qtyListenersAttached = true;
      function isOurFormQty(el) {
        if (!el || !formId) return false;
        var formEl = el.form || (el.getAttribute && el.closest && el.closest('form'));
        if (formEl && formEl.id === formId) return true;
        if (el.getAttribute && el.getAttribute('form') === formId) return true;
        return false;
      }
      document.addEventListener('click', function (ev) {
        if (ev.target && ev.target.closest && ev.target.closest('quantity-selector')) {
          var sel = ev.target.closest('quantity-selector');
          var formEl = sel.closest('form');
          var buyButtons = sel.closest('.product-form__buy-buttons');
          var isOurForm = (formEl && formEl.id === formId) || (buyButtons && wrap && buyButtons.contains(wrap));
          if (isOurForm && wrap && document.contains(wrap)) {
            setTimeout(recalc, 0);
          }
        }
      }, true);
      document.addEventListener('input', function (ev) {
        if (ev.target && ev.target.name === 'quantity' && isOurFormQty(ev.target) && wrap && document.contains(wrap)) {
          recalc();
        }
      }, true);
      document.addEventListener('change', function (ev) {
        if (ev.target && ev.target.name === 'quantity' && isOurFormQty(ev.target) && wrap && document.contains(wrap)) {
          recalc();
        }
      }, true);
    }

    attachFormQtyListeners();
    recalc();

    wrap._dtfRecalc = recalc;

    /* CloudLift/Live Product Options: use app's calculated price (size-based formula) */
    function onOptionsPrice(ev) {
      var priceData = ev.detail;
      if (!priceData) return;
      var q = getQtyFromForm();
      var unitPrice = null;
      /* priceData may have: price (cents), total (cents), unit_price, etc. */
      if (typeof priceData.unit_price === 'number' && priceData.unit_price > 0) {
        unitPrice = priceData.unit_price >= 1 ? priceData.unit_price / 100 : priceData.unit_price;
      } else if (typeof priceData.price === 'number' && priceData.price > 0) {
        unitPrice = priceData.price >= 1 ? priceData.price / 100 : priceData.price;
      } else if (typeof priceData.total === 'number' && priceData.total > 0 && q >= 1) {
        var totalCents = priceData.total;
        unitPrice = (totalCents / 100) / q;
      }
      if (unitPrice != null && unitPrice > 0) {
        setCloudliftBasePrice(unitPrice);
        recalc();
      }
    }
    window.addEventListener('options:price', onOptionsPrice);
    /* option:changed may precede options:price; recalc after a short delay so we pick up new price */
    window.addEventListener('option:changed', function () {
      setTimeout(recalc, 100);
    });

    /* When variant changes, update price-per-sq-in and recalc */
    var formEl = formId ? document.getElementById(formId) : null;
    if (formEl) {
      formEl.addEventListener('variant:changed', function (ev) {
        var variant = ev.detail && ev.detail.variant;
        if (variant && typeof variant.price === 'number' && wrap) {
          wrap.setAttribute('data-variant-price-cents', String(variant.price));
          clearCloudliftBasePrice();
          recalc();
        }
      });
    }

    function isDimensionInput(el) {
      if (!el || el.nodeName !== 'INPUT') return false;
      var n = (el.name || '').toLowerCase();
      if (n.indexOf('_dimension_x') !== -1 || n.indexOf('_dimension_y') !== -1) return true;
      if (n.indexOf('dimension') !== -1 && (n.indexOf('x') !== -1 || n.indexOf('y') !== -1)) return true;
      var container = el.closest && el.closest('[data-option="dimension"]');
      return !!container;
    }

    document.addEventListener('input', function onDocInput(ev) {
      if (isDimensionInput(ev.target) && wrap._dtfRecalc) wrap._dtfRecalc();
    }, true);
    document.addEventListener('change', function onDocChange(ev) {
      if (isDimensionInput(ev.target) && wrap._dtfRecalc) wrap._dtfRecalc();
    }, true);

    /* Poll quantity to ensure Price (ea) and Total update when + / - buttons change it (backup for any missed events) */
    var lastQty = getQtyFromForm();
    var qtyPollId = setInterval(function () {
      if (!wrap || !document.contains(wrap)) return;
      var q = getQtyFromForm();
      if (q !== lastQty) {
        lastQty = q;
        recalc();
      }
    }, 50);

    /* Poll dimensions – upload app may set values without firing input/change events */
    var lastDimKey = '';
    var dimPollId = setInterval(function () {
      if (!wrap || !document.contains(wrap)) return;
      var dims = getWidthHeightFromForm();
      var fromHidden = getDimensionFromHidden(formId);
      var key = (dims.w || 0) + 'x' + (dims.h || 0) + (fromHidden ? 'h' + fromHidden.w + 'x' + fromHidden.h : '');
      if (key !== lastDimKey) {
        lastDimKey = key;
        if (dims.w > 0.01 || dims.h > 0.01 || (fromHidden && fromHidden.w > 0.01)) recalc();
      }
    }, 400);

    /* Fallback: CloudLift may update [data-live-price] element; parse and use if options:price never fired */
    var lastParsedPrice = null;
    var cloudliftFallbackPollId = setInterval(function () {
      if (!wrap || !document.contains(wrap) || cloudliftUnitPrice != null) return;
      var el = document.querySelector('[data-dtf-cloudlift-price-fallback]');
      if (!el || !el.textContent) return;
      var txt = (el.textContent || '').trim().replace(/,/g, '');
      var match = txt.match(/[\d.]+/);
      if (match) {
        var parsed = parseFloat(match[0], 10);
        if (!isNaN(parsed) && parsed > 0 && parsed !== lastParsedPrice) {
          lastParsedPrice = parsed;
          var unitPrice = (parsed >= 1000) ? parsed / 100 : parsed;
          if (unitPrice > 0.01 && unitPrice < 1000) {
            setCloudliftBasePrice(unitPrice);
            recalc();
          }
        }
      }
    }, 500);

    return true;
  }

  function hasUploadedImage() {
    var root = document.querySelector('.filepond--root');
    if (!root) return false;
    return root.querySelector('.filepond--item') != null;
  }

  function getQtyRow() {
    var wrap = document.getElementById('dtf-price-calculator-root');
    if (!wrap) return null;
    var container = wrap.closest('.product-form__buy-buttons');
    if (!container) return null;
    var row = container.querySelector('.product-form__quantity-row:not(.product-form__quantity-row--dtf-above)');
    return row || null;
  }

  function getDtfQtyRow() {
    var wrap = document.getElementById('dtf-price-calculator-root');
    if (!wrap) return null;
    var container = wrap.closest('.product-form__buy-buttons');
    return container ? container.querySelector('.product-form__quantity-row--dtf-above') : null;
  }

  function showCalculator() {
    var wrap = document.getElementById('dtf-price-calculator-root');
    if (wrap) {
      wrap.classList.remove('dtf-calc--hidden');
      wrap.hidden = false;
    }
    var qtyRow = getQtyRow();
    if (qtyRow) qtyRow.style.display = 'none';
    var dtfQty = getDtfQtyRow();
    if (dtfQty) dtfQty.style.display = 'flex';
  }

  function hideCalculator() {
    var wrap = document.getElementById('dtf-price-calculator-root');
    if (wrap) {
      wrap.classList.add('dtf-calc--hidden');
      wrap.hidden = true;
    }
    var qtyRow = getQtyRow();
    if (qtyRow) qtyRow.style.display = 'flex';
    var dtfQty = getDtfQtyRow();
    if (dtfQty) dtfQty.style.display = 'none';
  }

  function pollForImage(cb) {
    var attempts = 0;
    var maxAttempts = 100;
    var interval = setInterval(function () {
      attempts++;
      if (hasUploadedImage()) {
        clearInterval(interval);
        if (cb) cb();
        return;
      }
      if (attempts >= maxAttempts) clearInterval(interval);
    }, 500);
  }

  function observeFilePond(onShow) {
    var root = document.querySelector('.filepond--root');
    if (!root) return;
    var obs = new MutationObserver(function () {
      if (hasUploadedImage()) {
        showCalculator();
        if (onShow) onShow();
      } else {
        hideCalculator();
      }
    });
    obs.observe(root, { childList: true, subtree: true });
    if (hasUploadedImage()) {
      showCalculator();
      if (onShow) onShow();
    }
  }

  var inited = false;
  var initRetries = 0;

  function run() {
    var wrap = document.getElementById('dtf-price-calculator-root');
    var handle = wrap && wrap.getAttribute('data-product-handle');
    if (!wrap || !getProductConfig(handle)) return;

    if (!inited) {
      inited = init();
    }

    if (!inited && initRetries < 10) {
      initRetries++;
      setTimeout(run, 300);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  document.addEventListener('DOMContentLoaded', run);
})();
